import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { exportCycleVillageExcel, exportCycleMemberExcel } from "./CycleYojnaExcel";
import {
  useVillageWiseSakhiMandalCount,
  useSakhiMandalWiseMemberDetailDataPagination
} from "../../../API_Master/FetchAPI";
import { useSakhiMandalMemberDetailExportData } from "../../../API_Master/ExcelAPI";
import { exportToExcel, notifyError } from "../../../MethodMaster/JSXComponentMothod";
import ExcelButton from "../../../CommonComponents/ExcelButton";
import PageEntries from "../../../CommonComponents/PageEntries";
import PaginationButtons from "../../../CommonComponents/PaginationButtons";
import TableShimmer from "../../../Components/TableShimmer";
import "../SakhiMandal/SakhiMandal.css";

const ShimmerCard = () => (
  <div className="govdash-villageCard-final">
    <div className="village-shimmer" style={{ height: "1rem", marginBottom: "0.6rem" }}></div>
    <div className="village-shimmer" style={{ height: "1.7rem" }}></div>
  </div>
);

const NoDataCard = () => (
  <div className="govdash-villageCard-final govdash-noVillageCard">
    <div className="govdash-noDataIcon">
      <i className="fas fa-village fa-2x"></i>
    </div>
    <div className="govdash-noDataTitle">No Data Found</div>
    <div className="govdash-noDataDesc">
      {document.querySelector(".govdash-searchInput")?.value ? "Try searching with different keywords" : "No data available"}
    </div>
  </div>
);

export default function CycleYojna() {
  const { DarkTheme } = useSelector((state) => state.ProjectSlice);
  const [search, setSearch] = useState("");
  const [selectedVillage, setSelectedVillage] = useState(null);
  const themes = ["glass-0", "glass-1", "glass-2", "glass-3", "glass-4"];

  // Filter states
  const [filterStatus, setFilterStatus] = useState(false);
  const filterRef = useRef(null);
  const [filterData, setFilterData] = useState({ studentID: "", studentName: "", studentCaste: "", year: "" });
  const [filterInput, setFilterInput] = useState({ studentID: "", studentName: "", studentCaste: "", year: "" });

  const { data: cycleYojnaData, isFetching: fetchingCycleYojnaData } = useVillageWiseSakhiMandalCount(!selectedVillage, "CycleYojna");

  // Pagination states for Members
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const { data: memberData, fetchNextPage, hasNextPage, isFetching: isMemberFetching, isLoading: isMemberLoading, isError: isMemberError } = useSakhiMandalWiseMemberDetailDataPagination(
    !!selectedVillage,
    rowsPerPage,
    "", // No Yojna Name required since we're fetching for the whole village
    selectedVillage?.Village || selectedVillage?.VillageName,
    filterData,
    "CycleYojna"
  );

  const { mutate: exportCycleYojnaMembers, isPending: isExportingMembers } = useSakhiMandalMemberDetailExportData();

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedVillage]);

  const pages = memberData?.pages || [];
  const totalCount = memberData?.pages[0]?.TotalCount || 0;
  const currentItems = pages[currentPage]?.data || [];
  const start = currentPage * rowsPerPage + 1;
  const end = Math.min(start + currentItems.length - 1, totalCount);

  const handleRowsChange = async (e) => {
    const limit = parseInt(e.target?.value);
    setRowsPerPage(limit);
    setCurrentPage(0);
  };

  const goToPage = async (index) => {
    if (pages[index]) {
      setCurrentPage(index);
    } else if (hasNextPage) {
      await fetchNextPage();
      setCurrentPage(index);
    }
  };

  const handleExport = async () => {
    toast.dismiss();
    if (!selectedVillage) {
      if (!filteredData.length) return notifyError("No Data Found");
      await exportCycleVillageExcel(filteredData);
    } else {
      if (totalCount === 0) return notifyError("No Data Found");

      exportCycleYojnaMembers(
        {
          sakhiMandalName: "",
          villageName: selectedVillage?.Village || selectedVillage?.VillageName,
          studentID: filterData.studentID || "",
          studentName: filterData.studentName || "",
          studentCaste: filterData.studentCaste || "",
          year: filterData.year || "",
          type: "CycleYojna"
        },
        {
          onSuccess: async (NotificationData) => {
            if (!NotificationData || !NotificationData.length) {
              return notifyError("No Data Found");
            }
            const villageName = selectedVillage?.Village || selectedVillage?.VillageName;
            await exportCycleMemberExcel(NotificationData, villageName);
          },
          onError: () => {
            return notifyError("Export failed. Please try again.");
          },
        }
      );
    }
  };

  const handleBackToVillages = () => {
    setSelectedVillage(null);
    setSearch("");
    setFilterData({ studentID: "", studentName: "", studentCaste: "", year: "" });
    setFilterInput({ studentID: "", studentName: "", studentCaste: "", year: "" });
    setFilterStatus(false);
  };

  const handleFilterSearch = () => {
    setFilterData(filterInput);
    setCurrentPage(0);
    setFilterStatus(false);
  };

  const handleFilterCancel = () => {
    const empty = { studentID: "", studentName: "", studentCaste: "", year: "" };
    setFilterInput(empty);
    setFilterData(empty);
    setCurrentPage(0);
    setFilterStatus(false);
  };

  // Compute filtered data at component level so handleExport can use it
  const filteredData = cycleYojnaData?.filter((v) => v.Village?.toLowerCase().includes(search.toLowerCase())) || [];

  // Level 2: Members Table View (Skipping the intermediate grid)
  if (selectedVillage) {
    return (
      <div className="flex-grow-1 w-100 mt-4">
        <div className="d-flex justify-content-between mb-3">
          <h3 className={"govdash-sectionTitle"}>વિદ્યાર્થીઓને સાયકલ સહાય યોજના (સરસ્વતી સાધના)</h3>
        </div>
        <button id="govdash-back-btn" className="govdash-backBtn mb-3" onClick={handleBackToVillages}>
          Back
        </button>

        <div className={`govdash-villageDetailHeader mt-4`}>
          <div onClick={handleBackToVillages} className={"govdash-detailIconWrapper cursor-pointer"}>
            <i className="govdash-VillageHeaderCard-icon fas fa-users"></i>
          </div>
          <div className={"govdash-detailInfo"}>
            <div className="d-flex align-items-center gap-2">
              <h2 className="govdash-detailName mb-0">{selectedVillage?.Village || selectedVillage?.VillageName}</h2>
            </div>
            <div className={"govdash-detailStats"}>
              <div className={"govdash-statItem "}>
                <span className={"govdash-statLabel"}>Student Count</span>
                <span className={"govdash-statValue"}>{totalCount || "0"}</span>
              </div>
            </div>
          </div>
        </div>

        {isMemberLoading || isMemberError || isMemberFetching ? (
          <TableShimmer theme={DarkTheme ? "dark" : "light"} />
        ) : (
          <div className="govdash-tableContainer mt-4">
            <div className="govdash-tableHeader">
              <h2 className="govdash-sectionTitle">
                Student List
              </h2>
              <div className="govdash-tableActions">
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem" }}>
                  <span style={{ whiteSpace: "nowrap" }}>Show</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsChange}
                    style={{
                      padding: "0.35rem 0.5rem",
                      borderRadius: "0.35rem",
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      fontSize: "0.8rem",
                      minWidth: "60px",
                    }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span style={{ whiteSpace: "nowrap" }}>entries</span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <button onClick={() => setFilterStatus(true)} className="govdash-btn-outline">
                    <i className="fas fa-filter"></i> Filter
                    {(filterData.studentID || filterData.studentName || filterData.studentCaste || filterData.year) && <span className="govdash-filterBadge">!</span>}
                  </button>
                  <ExcelButton isExportingMain={isExportingMembers} handleExportMain={handleExport} primaryExcel="Export to Excel" />
                </div>
              </div>
            </div>

            <div className="govdash-tableWrapper">
              <table className="govdash-table">
                <thead className="table-header-light border-top" id="tblHead">
                  <tr>
                    <th style={{ width: "5%" }}>SR NO</th>
                    <th style={{ width: "15%" }}>STUDENT ID</th>
                    <th style={{ width: "20%" }}>STUDENT NAME</th>
                    <th style={{ width: "10%" }}>INCOME</th>
                    <th style={{ width: "15%" }}>STUDENT CASTE</th>
                    <th style={{ width: "10%" }}>YEAR</th>
                  </tr>
                </thead>
                <tbody>
                  {pages[currentPage]?.data?.map((member, idx) => {
                    const absoluteIndex = currentPage * rowsPerPage + idx + 1;

                    return (
                      <React.Fragment key={`member-${idx}`}>
                        <tr className="align-top">
                          <td>{absoluteIndex}</td>
                          <td>{member?.StudentID || member?.MemberCode || "-"}</td>
                          <td>{member?.MemberName || member?.StudentName || "-"}</td>
                          <td>{member?.Income || "-"}</td>
                          <td>{member?.StudentCaste || "-"}</td>
                          <td>{member?.Year || "-"}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              <div className="row mx-3 justify-content-between">
                <PageEntries start={start} end={end} totalCount={totalCount} />
                <PaginationButtons
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  goToPage={goToPage}
                  pages={pages}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                />
              </div>
            </div>
          </div>
        )}

        {/* Filter Sidebar */}
        <div className={`govdash-filterSidebar ${filterStatus ? "open" : ""}`} ref={filterRef}>
          <div className="govdash-filterHeader">
            <span>
              <i className="fas fa-filter"></i> Filter Student
            </span>
            <button onClick={() => setFilterStatus(false)} className="govdash-closeBtn">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="govdash-filterBody">
            <div className="govdash-inputGroup">
              <label>Student ID</label>
              <input
                type="text"
                placeholder="Enter Student ID..."
                value={filterInput.studentID}
                onChange={(e) => setFilterInput((p) => ({ ...p, studentID: e.target.value }))}
              />
            </div>
            <div className="govdash-inputGroup">
              <label>Student Name</label>
              <input
                type="text"
                placeholder="Enter Student Name..."
                value={filterInput.studentName}
                onChange={(e) => setFilterInput((p) => ({ ...p, studentName: e.target.value }))}
              />
            </div>
            <div className="govdash-inputGroup">
              <label>Student Caste</label>
              <input
                type="text"
                placeholder="Enter Student Caste..."
                value={filterInput.studentCaste}
                onChange={(e) => setFilterInput((p) => ({ ...p, studentCaste: e.target.value }))}
              />
            </div>
            <div className="govdash-inputGroup">
              <label>Year</label>
              <input
                type="text"
                placeholder="Enter Year..."
                value={filterInput.year}
                onChange={(e) => setFilterInput((p) => ({ ...p, year: e.target.value }))}
              />
            </div>
          </div>
          <div className="govdash-filterFooter">
            <button onClick={handleFilterCancel} className="govdash-btn-secondary">
              Clear
            </button>
            <button onClick={handleFilterSearch} className="govdash-btn-primary">
              Apply Filters
            </button>
          </div>
        </div>

        {/* Overlay */}
        {filterStatus && <div className="govdash-sidebarOverlay" onClick={() => setFilterStatus(false)}></div>}
      </div>
    );
  }

  // Level 1: Village Grid View
  return (
    <>
      <div className="d-flex justify-content-between mt-3">
        <h3 className={"govdash-sectionTitle"}>વિદ્યાર્થીઓને સાયકલ સહાય યોજના (સરસ્વતી સાધના)</h3>
        <div className="d-flex gap-2">
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div className="govdash-searchWrapper">
              <i className="fas fa-search "></i>
              <input type="text" placeholder="Search Village Name..." value={search} onChange={(e) => setSearch(e.target.value)} className="govdash-searchInput" />
              {search && (
                <button onClick={() => setSearch("")} className="govdash-clearSearch">
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
          <div>
            <button onClick={handleExport} className="govdash-excelBtn mt-4">
              <i className="fas fa-file-excel"></i>
              <span className="ms-2">Export Excel</span>
            </button>
          </div>
        </div>
      </div>

      <div className="govdash-villageGrid-final mt-4">
        {fetchingCycleYojnaData ? (
          Array(8)
            .fill()
            .map((_, i) => <ShimmerCard key={i} />)
        ) : filteredData.length > 0 ? (
          filteredData.map((v, idx) => {
            const theme = themes[idx % 5];
            const mandaliCount = v.TotalSakhiMandal || v.TotalMandali || v.MandaliCount || "0";
            const memberCount = v.TotalMember || v.MemberCount || v.TotalMembers || "0";
            const villageName = v.Village || v.VillageName || "Unknown";

            return (
              <div key={idx} className={`govdash-villageCard-final yojna-${theme}`} onClick={() => { setSelectedVillage(v); setSearch(""); }}>
                <div className="village-header-final">
                  <div className="village-name-final">{villageName}</div>
                </div>

                <div className="member-card-minimal">
                  <div className="member-card-content">
                    <span className="member-card-label">Student Count</span>
                    <span className="member-card-value">{memberCount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <NoDataCard />
        )}
      </div>
    </>
  );
}
